import { Company } from '@apis/Customers/model';
import { postTagPolicyQueryAwsPolicies, postTagPolicyQueryAzurePolicies } from '@apis/Resources';
import { AwsEffectiveTagPolicy, AzureTagPolicy } from '@apis/Resources/model';
import { CompanyTenantPrereqService } from '@root/Components/Router/CompanyContent';
import { inject, injectable, singleton } from 'tsyringe';
import { BaseCacheByTenant } from '../Customers/BaseCacheByTenant';
import { ICompanyContextToken } from '../Customers/CompanyContext';
import { Platform } from '../PlatformService';
import { queryBuilder } from '../QueryExpr';

interface ITagPolicyItem {
    platform: Platform;
    accountId: string;
    accountType: 'Account' | 'Subscription';
    tag: string;
    compatible: boolean;
}

class TagPolicyLookup {
    private readonly tagsByPlatform = new Map<Platform, Set<string>>();
    private readonly policiesByTag = new Map<string, ITagPolicyItem[]>();
    private readonly policiesByPlatformTag = new Map<string, ITagPolicyItem[]>();
    private readonly platforms = new Set<Platform>();
    private readonly tags = new Set<string>();
    private readonly policies: ITagPolicyItem[] = [];

    public constructor(items: ITagPolicyItem[]) {
        this.policies = items;
        for (const item of items) {
            const platformTags =
                this.tagsByPlatform.get(item.platform) ?? this.tagsByPlatform.set(item.platform, new Set<string>()).get(item.platform)!;
            platformTags.add(item.tag);

            const tagPolicies = this.policiesByTag.get(item.tag) ?? this.policiesByTag.set(item.tag, []).get(item.tag)!;
            tagPolicies.push(item);

            const ptKey = this.getPlatformTagKey(item.platform, item.tag);
            const ptPolicies = this.policiesByPlatformTag.get(ptKey) ?? this.policiesByPlatformTag.set(ptKey, []).get(ptKey)!;
            ptPolicies.push(item);

            this.platforms.add(item.platform);
            this.tags.add(item.tag);
        }
    }

    public getAllPolicies() {
        return this.policies;
    }
    public getPlatformTags(platform: Platform) {
        return this.tagsByPlatform.get(platform) ?? new Set<string>();
    }
    public getPoliciesByTag(tag: string) {
        return this.policiesByTag.get(tag) ?? [];
    }
    public getPolicies(platform: Platform, tag: string) {
        return this.policiesByPlatformTag.get(this.getPlatformTagKey(platform, tag));
    }
    public getPlatforms() {
        return this.platforms;
    }
    public getTags() {
        return this.tags;
    }

    private getPlatformTagKey(platform: Platform, tag: string) {
        return `${platform}-${tag}`;
    }
}

export interface ITagPolicyLookup extends TagPolicyLookup {}

@singleton()
class TagPolicyListCache extends BaseCacheByTenant<ITagPolicyLookup> {
    public constructor(@inject(CompanyTenantPrereqService) tenantPrereqSvc: CompanyTenantPrereqService) {
        super(tenantPrereqSvc);
    }
}

@injectable()
export class TagPolicyService {
    public constructor(
        @inject(TagPolicyListCache) private readonly policyCache: TagPolicyListCache,
        @inject(ICompanyContextToken) private readonly company: Company
    ) {}

    public async getPolicyLookup() {
        return await this.policyCache.get(this.company.Id!, async () => {
            const platformPolicies = await Promise.all([this.getAwsPolicyInfo(), this.getAzurePolicyInfo()]);
            return new TagPolicyLookup(platformPolicies.flatMap((v) => v));
        });
    }

    private async getAwsPolicyInfo() {
        const results = await queryBuilder<AwsEffectiveTagPolicy>()
            .select((b) => ({
                tag: b.model.Tags! as unknown as string,
                accountId: b.model.TargetId!,
                ct: b.count(),
            }))
            .execute(postTagPolicyQueryAwsPolicies);

        return (
            results?.Results?.map(
                (r) =>
                    ({
                        ...r,
                        accountType: 'Account',
                        compatible: true,
                        platform: 'Aws',
                    } as ITagPolicyItem)
            ) ?? []
        );
    }

    private async getAzurePolicyInfo() {
        const results = await queryBuilder<AzureTagPolicy>()
            .select((b) => ({
                tag: b.model.Tags! as unknown as string,
                accountId: b.model.SubscriptionId!,
                compatible: b.model.LogicSupported,
                ct: b.count(),
            }))
            .execute(postTagPolicyQueryAzurePolicies);

        return (
            results?.Results?.map(
                (r) =>
                    ({
                        ...r,
                        accountType: 'Subscription',
                        platform: 'Azure',
                    } as ITagPolicyItem)
            ) ?? []
        );
    }
}
